/**
 * Type-safe query key factory.
 *
 * Provides a centralized, type-safe way to generate query keys
 * for React Query. This ensures consistency and enables
 * efficient cache invalidation.
 *
 * @example
 * ```ts
 * // Use in a query
 * useQuery({
 *   queryKey: queryKeys.tokenPairs.byNetwork(25),
 *   queryFn: () => fetchPairs(25),
 * });
 *
 * // Invalidate all tokenPairs queries
 * queryClient.invalidateQueries({ queryKey: queryKeys.tokenPairs.all });
 *
 * // Invalidate specific network
 * queryClient.invalidateQueries({ queryKey: queryKeys.tokenPairs.byNetwork(25) });
 * ```
 */
export const queryKeys = {
    /** Root key for all queries */
    all: ['all'] as const,

    /** Token pairs related queries */
    tokenPairs: {
        /** All token pairs queries */
        all: ['tokenPairs'] as const,

        /** Token pairs for a specific network */
        byNetwork: (networkId: number) =>
            [...queryKeys.tokenPairs.all, 'network', networkId] as const,

        /** Detailed trading rules for a specific pair */
        detail: (networkId: number, pair: string) =>
            [...queryKeys.tokenPairs.all, 'detail', networkId, pair] as const,

        /** Leverage-specific constraints for a specific pair */
        leverageDetail: (networkId: number, pair: string, leverage: number) =>
            [
                ...queryKeys.tokenPairs.all,
                'leverageDetail',
                networkId,
                pair,
                leverage,
            ] as const,
    },

    /** Trade history queries */
    trades: {
        /** All trade history queries */
        all: ['trades'] as const,

        /** Closed/terminal trades for the history panel, scoped to a network */
        history: (networkId: number) =>
            [...queryKeys.trades.all, 'history', networkId] as const,

        /** Open/Pending trades that the user can still close */
        active: (networkId: number) =>
            [...queryKeys.trades.all, 'active', networkId] as const,

        /**
         * Hyperliquid positions / resting orders that NO trade row covers.
         * Under `trades` on purpose: everything that changes the trade rows
         * (a close, a limit cancel, a status push) can change this too, so the
         * two are invalidated side by side.
         */
        exposure: (networkId: number) =>
            [...queryKeys.trades.all, 'exposure', networkId] as const,
    },

    /** Speed-trading (Hyperliquid) backend queries — all on networkId 999 */
    speed: {
        /** All speed-trading queries */
        all: ['speed'] as const,

        /** Public runtime config + fee-path readiness */
        config: () => [...queryKeys.speed.all, 'config'] as const,

        /** Public bridge registry: per-chain failover order for the testing switch */
        bridges: () => [...queryKeys.speed.all, 'bridges'] as const,

        /** Public HyperEVM chain-load / congestion telemetry */
        gas: () => [...queryKeys.speed.all, 'gas'] as const,

        /** Custodial + adapter + hypercore USDC totals for the user */
        balance: () => [...queryKeys.speed.all, 'balance'] as const,

        /** The user's custodial wallet + adapter addresses */
        wallet: () => [...queryKeys.speed.all, 'wallet'] as const,

        /** The user's Hyperliquid agent-key state (reactivation banner) */
        agentStatus: () => [...queryKeys.speed.all, 'agentStatus'] as const,

        /** Pre-trade order quote (size, limit price, notional) */
        quote: (
            coin: string,
            leverage: number,
            collateralUsd: number,
            slippageBps: number,
        ) =>
            [
                ...queryKeys.speed.all,
                'quote',
                coin,
                leverage,
                collateralUsd,
                slippageBps,
            ] as const,

        /** Live adapter position proof for a coin */
        position: (coin: string) =>
            [...queryKeys.speed.all, 'position', coin] as const,

        /** Every open position in one read — the markets not on the chart */
        positions: () => [...queryKeys.speed.all, 'positions'] as const,

        /** Deposit saga status */
        deposit: (transferId: number) =>
            [...queryKeys.speed.all, 'deposit', transferId] as const,

        /** Withdraw saga status */
        withdraw: (transferId: number) =>
            [...queryKeys.speed.all, 'withdraw', transferId] as const,

        /** Last speed operation jobs for the authenticated user */
        jobs: () => [...queryKeys.speed.all, 'jobs'] as const,

        /** What a V1 to V2 migration started now would move (V1 only) */
        migrateQuote: () => [...queryKeys.speed.all, 'migrateQuote'] as const,

        /** Public liquidation clusters for a market (market-wide sample) */
        liqMap: (coin: string) =>
            [...queryKeys.speed.all, 'liqMap', coin.toUpperCase()] as const,
    },

    /** Trading-window (authority) queries */
    tradingSession: {
        /** All trading-session queries */
        all: ['tradingSession'] as const,

        /**
         * Whether BOTH custodial wallets (Cronos authorization + HyperEVM
         * trading) already exist on chain for an owner EOA. Two `wallets(owner)`
         * reads, shared by everything that needs to know whether this wallet
         * finished onboarding.
         */
        walletPresence: (owner: string) =>
            [...queryKeys.tradingSession.all, 'walletPresence', owner] as const,
    },
} as const;

/**
 * Type helper for extracting query key types.
 */
export type QueryKeys = typeof queryKeys;
